import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';

export default function SystemNotificationManagerModal({ isOpen, onClose }) {
    const { t } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingNotificationId, setEditingNotificationId] = useState(null);
    const [filterTerm, setFilterTerm] = useState('');

    const loadUsers = useCallback(async () => {
        try {
            const data = await apiService.getAllUsersForManagement();
            setUsers(data);
        } catch (err) {
            setError(err.message || t('systemNotifications.loadUsersFailed'));
        }
    }, [t]);

    const loadNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiService.getSystemNotifications();
            setNotifications(data);
        } catch (err) {
            setError(err.message || t('systemNotifications.loadNotificationsFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setError('');
        setSuccessMessage('');
        loadUsers();
        loadNotifications();
    }, [isOpen, loadUsers, loadNotifications]);

    const resetForm = () => {
        setTitle('');
        setContent('');
        setSelectedUserIds([]);
        setEditingNotificationId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!title.trim() || !content.trim()) {
            setError(t('systemNotifications.requiredFields'));
            return;
        }
        if (selectedUserIds.length === 0) {
            setError(t('systemNotifications.selectTargetsWarning'));
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingNotificationId) {
                await apiService.updateSystemNotification(editingNotificationId, {
                    title,
                    content,
                    notificationType: 'Update',
                    targetUserIds: selectedUserIds
                });
                setSuccessMessage(t('systemNotifications.updateSuccess'));
            } else {
                await apiService.createSystemNotification({
                    title,
                    content,
                    notificationType: 'Update',
                    targetUserIds: selectedUserIds
                });
                setSuccessMessage(t('systemNotifications.createSuccess'));
            }
            resetForm();
            await loadNotifications();
        } catch (err) {
            setError(err.message || t('systemNotifications.saveFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (notification) => {
        if (notification.status === 'Sent') {
            return;
        }
        setEditingNotificationId(notification.id);
        setTitle(notification.title);
        setContent(notification.content);
        setSelectedUserIds(notification.targets.map(t => t.id));
        setSuccessMessage('');
        setError('');
    };

    const handleSend = async (notificationId) => {
        setError('');
        setSuccessMessage('');
        try {
            await apiService.sendSystemNotification(notificationId);
            setSuccessMessage(t('systemNotifications.sendSuccess'));
            await loadNotifications();
        } catch (err) {
            setError(err.message || t('systemNotifications.sendFailed'));
        }
    };

    const handleDelete = async (notificationId) => {
        if (!window.confirm(t('systemNotifications.deleteConfirm'))) {
            return;
        }
        setError('');
        setSuccessMessage('');
        try {
            await apiService.deleteSystemNotification(notificationId);
            setSuccessMessage(t('systemNotifications.deleteSuccess'));
            await loadNotifications();
            if (editingNotificationId === notificationId) {
                resetForm();
            }
        } catch (err) {
            setError(err.message || t('systemNotifications.deleteFailed'));
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const filteredUsers = useMemo(() => {
        if (!filterTerm.trim()) {
            return users;
        }
        const keyword = filterTerm.toLowerCase();
        return users.filter(user =>
            user.username.toLowerCase().includes(keyword) ||
            user.role.toLowerCase().includes(keyword)
        );
    }, [users, filterTerm]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex justify-between items-center">
                    <h2 className="text-xl font-semibold">{t('systemNotifications.managerTitle')}</h2>
                    <button onClick={() => { resetForm(); onClose(); }} className="text-2xl font-bold hover:text-gray-200">×</button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 text-red-700 text-sm">{error}</div>
                    )}
                    {successMessage && (
                        <div className="bg-green-50 border-l-4 border-green-400 p-4 text-green-700 text-sm">{successMessage}</div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                {editingNotificationId ? t('systemNotifications.editNotification') : t('systemNotifications.createNotification')}
                            </h3>
                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('systemNotifications.title')}</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={t('systemNotifications.titlePlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('systemNotifications.content')}</label>
                                    <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        rows={6}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={t('systemNotifications.contentPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('systemNotifications.targets')}</label>
                                    <input
                                        type="text"
                                        value={filterTerm}
                                        onChange={(e) => setFilterTerm(e.target.value)}
                                        placeholder={t('systemNotifications.filterUsers')}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white">
                                        {filteredUsers.map(user => (
                                            <label key={user.id} className="flex items-center px-3 py-2 hover:bg-gray-100 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUserIds.includes(user.id)}
                                                    onChange={() => toggleUserSelection(user.id)}
                                                    className="mr-2"
                                                />
                                                <span className="font-medium">{user.username}</span>
                                                <span className="ml-2 text-xs text-gray-500">[{user.role}]</span>
                                            </label>
                                        ))}
                                        {filteredUsers.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-gray-400">{t('systemNotifications.noUsers')}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    {editingNotificationId ? (
                                        <button
                                            type="button"
                                            className="text-sm text-gray-500 hover:text-gray-700"
                                            onClick={resetForm}
                                        >
                                            {t('systemNotifications.cancelEdit')}
                                        </button>
                                    ) : <span />}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? t('systemNotifications.saving') : (editingNotificationId ? t('systemNotifications.updateButton') : t('systemNotifications.createButton'))}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">{t('systemNotifications.notificationList')}</h3>
                                <button onClick={loadNotifications} className="text-sm text-blue-600 hover:text-blue-700">
                                    {t('systemNotifications.refresh')}
                                </button>
                            </div>
                            {loading ? (
                                <div className="text-center text-sm text-gray-500 py-10">{t('loading')}</div>
                            ) : notifications.length === 0 ? (
                                <div className="text-center text-gray-400 text-sm py-10">{t('systemNotifications.emptyState')}</div>
                            ) : (
                                <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                                    {notifications.map(notification => (
                                        <div key={notification.id} className="border border-gray-200 rounded-md p-4 bg-gray-50">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h4 className="text-md font-semibold text-gray-900">{notification.title}</h4>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {t('systemNotifications.createdAt', { date: new Date(notification.createdAt).toLocaleString() })}
                                                        {notification.sentTime && (
                                                            <>
                                                                {' • '}
                                                                {t('systemNotifications.sentAt', { date: new Date(notification.sentTime).toLocaleString() })}
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${notification.status === 'Sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                    {notification.status === 'Sent' ? t('systemNotifications.status.sent') : t('systemNotifications.status.draft')}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-700 mt-3 whitespace-pre-line">{notification.content}</p>
                                            <div className="mt-3">
                                                <div className="text-xs text-gray-500 mb-1">{t('systemNotifications.recipients')}</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {notification.targets.map(target => (
                                                        <span
                                                            key={target.id}
                                                            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700 flex items-center gap-2"
                                                        >
                                                            <span>
                                                                {target.username}
                                                                <span className="ml-1 text-gray-400">[{target.role}]</span>
                                                            </span>
                                                            {typeof target.isRead === 'boolean' && (
                                                                <span className={`inline-flex items-center gap-1 ${target.isRead ? 'text-green-600' : 'text-orange-600'}`}>
                                                                    <span className={`inline-block w-2 h-2 rounded-full ${target.isRead ? 'bg-green-500' : 'bg-orange-400'}`}></span>
                                                                    {target.isRead ? t('systemNotifications.read') : t('systemNotifications.unread')}
                                                                    {target.isRead && target.readAt && (
                                                                        <span className="text-gray-400">
                                                                            ({t('systemNotifications.readAt', { date: new Date(target.readAt).toLocaleString() })})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex gap-3 mt-4">
                                                {notification.status !== 'Sent' && (
                                                    <button
                                                        onClick={() => handleEdit(notification)}
                                                        className="text-sm text-blue-600 hover:text-blue-700"
                                                    >
                                                        {t('systemNotifications.editButton')}
                                                    </button>
                                                )}
                                                {notification.status !== 'Sent' && (
                                                    <button
                                                        onClick={() => handleSend(notification.id)}
                                                        className="text-sm text-green-600 hover:text-green-700"
                                                    >
                                                        {t('systemNotifications.sendButton')}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(notification.id)}
                                                    className="text-sm text-red-600 hover:text-red-700"
                                                >
                                                    {t('systemNotifications.deleteButton')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
